import { IDisplayItem, IKuikaStyle } from "@kuika/kuika-cl-model";
import React, { useMemo } from "react";
import { getOutsideStyleWriteLock } from "../../right-panel/styling-panel/styling-panel-mode-store";
import {
  applyStyleTagIds,
  buildUnifiedStyleList,
  getAppliedStyleTagIds,
  IUnifiedStyleItem,
  removeStyleTagIds,
  toFormStyleIds
} from "../../right-panel/styling-panel/unified-styling-selector/style-tag-helpers";
import "./quick-props/quick-props.scss";
import {
  IQuickOption,
  QuickChip,
  QuickDismissContext,
  QuickLock,
  QuickOptionList,
  QuickPopover,
  QuickTip,
  useQuickOpenState
} from "./quick-props/quick-controls";
import { commitQuickStyle } from "./quick-props/quick-writes";
import { ISelectionOverlaySd } from "./selection-actions";
import { useStylingPanelMode } from "./use-styling-panel-mode";

export interface IStyleTagSlotProps {
  sd: ISelectionOverlaySd;
  kuikaId: string;
  displayItem: IDisplayItem;
  /** Bumped by the overlay to close the popover. */
  dismissSignal: number;
}

const NONE_VALUE: string = "__none__";

/**
 * The next styleObj after toggling one tag (or clearing all with `null`): the styling panel's own
 * add/remove rules (`style-tag-helpers.ts`) applied to the component's styleObj, tag ids hydrated first
 * so the commit's id rebuild keeps the tags that stay.
 */
export function toggleStyleTag(
  styleObj: IKuikaStyle | undefined,
  list: IUnifiedStyleItem[],
  tagId: string | null
): IKuikaStyle {
  const next: IKuikaStyle = toFormStyleIds(styleObj);
  const applied: string[] = getAppliedStyleTagIds(next, list);
  const byId = (id: string): IUnifiedStyleItem | undefined =>
    list.find((item: IUnifiedStyleItem): boolean => item.id.toLowerCase() === id.toLowerCase());
  if (tagId === null) {
    applied.forEach((id: string): void => {
      const item: IUnifiedStyleItem | undefined = byId(id);
      if (item) {
        removeStyleTagIds(next, item);
      }
    });
    return next;
  }
  const item: IUnifiedStyleItem | undefined = byId(tagId);
  if (!item) {
    return next;
  }
  if (applied.some((id: string): boolean => id.toLowerCase() === tagId.toLowerCase())) {
    removeStyleTagIds(next, item);
  } else {
    applyStyleTagIds(next, item);
  }
  return next;
}

const StyleTagChip = ({ sd, kuikaId, displayItem }: Omit<IStyleTagSlotProps, "dismissSignal">): JSX.Element => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  const list: IUnifiedStyleItem[] = useMemo(
    (): IUnifiedStyleItem[] => (sd.project ? buildUnifiedStyleList(sd.project) : []),
    [sd.project]
  );
  const formStyle: IKuikaStyle = toFormStyleIds(displayItem.styleObj);
  const applied: string[] = getAppliedStyleTagIds(formStyle, list);
  const appliedItems: IUnifiedStyleItem[] = applied
    .map((id: string): IUnifiedStyleItem | undefined => list.find((item: IUnifiedStyleItem): boolean => item.id === id))
    .filter((item: IUnifiedStyleItem | undefined): item is IUnifiedStyleItem => item !== undefined);
  const first: IUnifiedStyleItem | undefined = appliedItems[0];
  // The chip is compact (it sits in the bar's never-folding section): icon only with no tag, else the
  // first tag's name, truncated; the full list of applied tags is in the tooltip.
  const label: string =
    appliedItems.length === 0
      ? "Style tag: none"
      : `Style tag: ${appliedItems.map((item: IUnifiedStyleItem): string => item.displayName).join(", ")}`;

  const commit = (tagId: string | null): void => {
    commitQuickStyle(kuikaId, (live: IKuikaStyle | undefined): IKuikaStyle => toggleStyleTag(live, list, tagId));
  };

  return (
    <QuickPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      testId="quickPropPopover-styleTag"
      align="end"
      content={
        <QuickOptionList
          title="Style tags"
          options={[
            { value: NONE_VALUE, label: "None" },
            ...list.map((item: IUnifiedStyleItem): IQuickOption => ({ value: item.id, label: item.displayName }))
          ]}
          current={appliedItems.length === 0 ? [NONE_VALUE] : applied}
          isSearchable={list.length > 6}
          emptyText="No style tags in this project"
          optionTestId={(o: IQuickOption): string =>
            o.value === NONE_VALUE ? "selectionStyleTagOption-None" : `selectionStyleTagOption-${o.label}`
          }
          onPick={(value: string): void => commit(value === NONE_VALUE ? null : value)}
        />
      }
    >
      <QuickTip label={label} isSuppressed={isOpen}>
        <QuickChip
          testId="selectionStyleTagChip"
          ariaLabel={label}
          icon="palette"
          variant="compact"
          value={first ? first.displayName : undefined}
          meta={appliedItems.length > 1 ? `+${appliedItems.length - 1}` : undefined}
          hasPopup
          isOpen={isOpen}
          onClick={(): void => setIsOpen(!isOpen)}
        />
      </QuickTip>
    </QuickPopover>
  );
};

/**
 * The selection bar's style-tag chip (generic right section, before the ⋯ menu): the tags the
 * component carries — they stack — and a list to toggle them, with exactly the styling panel's rules
 * (shared through `style-tag-helpers.ts`). Committed as `ChangeStyleValueExternal`, so an open styling
 * panel reloads its form.
 */
export const StyleTagSlot: React.FC<IStyleTagSlotProps> = ({
  dismissSignal,
  ...rest
}: IStyleTagSlotProps): JSX.Element => {
  // Same lock as the quick style controls: a tag toggled here while the styling panel previews a state
  // or edits a tag would be overwritten when the panel restores / commits its own copy.
  const lockReason: string | null = getOutsideStyleWriteLock(useStylingPanelMode(), rest.kuikaId);
  return (
    <QuickDismissContext.Provider value={dismissSignal}>
      <QuickLock reason={lockReason} testId="selectionStyleTagLocked">
        <StyleTagChip {...rest} />
      </QuickLock>
    </QuickDismissContext.Provider>
  );
};
